import React from 'react';
import { Button } from '@/components/ui/button';
import {
  Table as TableIcon,
  ChartBar,
  ChartLine,
  ChartPie,
  ChartDonut,
  FloppyDisk,
  SortAscending,
  SortDescending,
  Equals,
} from '@phosphor-icons/react';
import DimensionSelector from './DimensionSelector';
import MeasureSelector from './MeasureSelector';
import {
  ReportFieldMetadata,
  ReportDimension,
  ReportMeasure,
  ReportViewType,
  ChartType,
} from 'shared/types/asset-report';

interface ReportToolbarProps {
  fields: ReportFieldMetadata[];
  viewType: ReportViewType;
  onViewTypeChange: (vt: ReportViewType) => void;
  chartType: ChartType;
  onChartTypeChange: (ct: ChartType) => void;
  rowDimension: ReportDimension | null;
  onRowDimensionChange: (d: ReportDimension | null) => void;
  stackDimension: ReportDimension | null;
  onStackDimensionChange: (d: ReportDimension | null) => void;
  measures: ReportMeasure[];
  onMeasuresChange: (m: ReportMeasure[]) => void;
  chartSort: 'none' | 'asc' | 'desc';
  onChartSortChange: (s: 'none' | 'asc' | 'desc') => void;
  onSave: () => void;
}

const CHART_TYPES: { type: ChartType; icon: React.ReactNode; label: string }[] = [
  { type: 'bar', icon: <ChartBar className="h-4 w-4" />, label: 'Staafdiagram' },
  { type: 'line', icon: <ChartLine className="h-4 w-4" />, label: 'Lijndiagram' },
  { type: 'pie', icon: <ChartPie className="h-4 w-4" />, label: 'Taartdiagram' },
  { type: 'doughnut', icon: <ChartDonut className="h-4 w-4" />, label: 'Ringdiagram' },
];

const ReportToolbar: React.FC<ReportToolbarProps> = ({
  fields,
  viewType,
  onViewTypeChange,
  chartType,
  onChartTypeChange,
  rowDimension,
  onRowDimensionChange,
  stackDimension,
  onStackDimensionChange,
  measures,
  onMeasuresChange,
  chartSort,
  onChartSortChange,
  onSave,
}) => {
  return (
    <div className="flex flex-wrap items-end gap-3 pb-4 border-b border-border">
      {/* View type toggle */}
      <div className="flex flex-col gap-1">
        <span className="text-xs font-medium text-muted-foreground">Weergave</span>
        <div className="flex bg-muted rounded-md p-0.5">
          <button
            onClick={() => onViewTypeChange('chart')}
            className={`flex items-center h-8 px-3 text-sm rounded-sm transition-colors ${
              viewType === 'chart'
                ? 'bg-background shadow-sm text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <ChartBar className="h-4 w-4 mr-1.5" />
            Grafiek
          </button>
          <button
            onClick={() => onViewTypeChange('pivot')}
            className={`flex items-center h-8 px-3 text-sm rounded-sm transition-colors ${
              viewType === 'pivot'
                ? 'bg-background shadow-sm text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <TableIcon className="h-4 w-4 mr-1.5" />
            Draaitabel
          </button>
        </div>
      </div>

      {/* Chart type selector (only in chart mode) */}
      {viewType === 'chart' && (
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">Grafiektype</span>
          <div className="flex bg-muted rounded-md p-0.5">
            {CHART_TYPES.map(ct => (
              <button
                key={ct.type}
                onClick={() => onChartTypeChange(ct.type)}
                title={ct.label}
                className={`flex items-center justify-center h-8 w-8 rounded-sm transition-colors ${
                  chartType === ct.type
                    ? 'bg-background shadow-sm text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {ct.icon}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Dimension selectors — only in chart mode */}
      {viewType === 'chart' && (
        <>
          <DimensionSelector
            label="Dimensie"
            fields={fields}
            value={rowDimension}
            onChange={onRowDimensionChange}
          />
          <DimensionSelector
            label="Gestapeld op"
            fields={fields}
            value={stackDimension}
            onChange={onStackDimensionChange}
          />
        </>
      )}

      {/* Measure selector */}
      <MeasureSelector
        fields={fields}
        value={measures}
        onChange={onMeasuresChange}
      />

      {/* Sort toggle — chart mode only */}
      {viewType === 'chart' && (
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">Sorteren</span>
          <div className="flex bg-muted rounded-md p-0.5">
            {([
              { value: 'none' as const, icon: <Equals className="h-4 w-4" />, title: 'Geen sortering' },
              { value: 'desc' as const, icon: <SortDescending className="h-4 w-4" />, title: 'Aflopend (hoog → laag)' },
              { value: 'asc' as const, icon: <SortAscending className="h-4 w-4" />, title: 'Oplopend (laag → hoog)' },
            ]).map(opt => (
              <button
                key={opt.value}
                onClick={() => onChartSortChange(opt.value)}
                title={opt.title}
                className={`flex items-center justify-center h-8 w-8 rounded-sm transition-colors ${
                  chartSort === opt.value
                    ? 'bg-background shadow-sm text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {opt.icon}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Save button */}
      <div className="flex flex-col gap-1 ml-auto">
        <span className="text-xs font-medium text-transparent">.</span>
        <Button variant="outline" size="sm" onClick={onSave} className="h-8">
          <FloppyDisk className="h-4 w-4 mr-1.5" />
          Opslaan
        </Button>
      </div>
    </div>
  );
};

export default ReportToolbar;
